import i18next from 'i18next';
import { AnyAction } from 'redux-saga';
import { all, takeEvery, put, select, call } from 'redux-saga/effects';
import { PapiRequestStudioBreadcrumbs, PapiStudioBreadcrumbs } from '@wix/da-papi-types';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import {
  logPageView,
  biModalUnload,
  type PageViewPayload,
} from '@wix/da-shared-react/pkg/biLogger/redux/actions';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { appMounted } from '@wix/da-shared-react/pkg/redux/app/actions';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { splashReady } from '@wix/da-submit-shared/pkg/redux/actions';
import { setNewModalLoading } from '../../actions/studio';
import {
  getActiveSection,
  getIsRootFolder,
  getPlaygroundSubsection,
  getPublishedTierId,
  getResultsTotalCount,
  getSelectedFolder,
  getStudioParams,
} from '../../selectors/studio';
import { getDiscountsCount } from '../../selectors/studio/discounts';
import { getHasAnyReportsData } from '../../selectors/reports';
import { getBiView, getBiComponent } from '../../helpers/bilogger';
import { fetchBreadcrumbs, setBreadcrumbs } from '../../actions/studio/page';
import { getIsVisitorView, getStashFolder, getHasDeviations } from '../../selectors/stash';
import { StudioSection } from '../../types/store';
import type { PageViewBiEvent } from '@wix/da-bi/pkg/events';

function* pageViewHandler(action: AnyAction) {
  // When user was linked to studio?new=1 we open the submit modal, which fires a page view event.
  // In that particular case we want to skip the studio page load event to avoid firing twice.
  const { requestUrl } = yield select(getEnv);
  const appWasMounted = action.type === appMounted.toString();
  const skipLog = appWasMounted && new URL(requestUrl).searchParams.get('new') === '1';
  if (skipLog) {
    return;
  }

  const activeSection = yield select(getActiveSection);
  const activePlaygroundSubsection = yield select(getPlaygroundSubsection);
  const isStashVisitorView = yield select(getIsVisitorView);
  const { contentType } = yield select(getStudioParams);

  const biCommon: Pick<PageViewBiEvent, 'view' | 'component'> = {
    view: getBiView(isStashVisitorView),
    component: getBiComponent(
      activeSection,
      activePlaygroundSubsection,
      isStashVisitorView,
      contentType
    ),
  };

  if (isStashVisitorView) {
    const stashFolder = yield select(getStashFolder);
    const stashFolderHasDeviations = yield select(getHasDeviations);
    yield put(
      logPageView({
        ...biCommon,
        is_empty_state_view: stashFolderHasDeviations ? 0 : 1,
        typeid: ResourceType.STASH_FOLDER,
        itemid: stashFolder.folderId,
      })
    );
    return;
  }

  const resultsTotalCount = yield select(getResultsTotalCount);
  const discountsCount = yield select(getDiscountsCount);
  const hasAnalyticsData = yield select(getHasAnyReportsData);
  const currentFolder: ReturnType<typeof getSelectedFolder> = yield select(getSelectedFolder);
  const isRootFolder: ReturnType<typeof getIsRootFolder> = yield select(getIsRootFolder);
  const tierId: ReturnType<typeof getPublishedTierId> = yield select(getPublishedTierId);

  let isEmptyState = false;
  if (
    activeSection === StudioSection.SELLER_DISCOUNTS_ACTIVE ||
    activeSection === StudioSection.SELLER_DISCOUNTS_SCHEDULED ||
    activeSection === StudioSection.SELLER_DISCOUNTS_ENDED ||
    activeSection === StudioSection.SELLER_DISCOUNTS_ALL
  ) {
    isEmptyState = discountsCount === 0;
  } else if (activeSection === StudioSection.SHOP_REPORTS) {
    isEmptyState = !hasAnalyticsData;
  } else {
    isEmptyState = resultsTotalCount === 0;
  }

  const currentSectionBiData: PageViewPayload = {
    ...biCommon,
    is_empty_state_view: isEmptyState ? 1 : 0,
  };

  switch (activeSection) {
    case StudioSection.STASH:
      if (isRootFolder || !currentFolder) {
        break;
      }
      currentSectionBiData.itemid = currentFolder.folderId;
      currentSectionBiData.typeid = ResourceType.STASH_FOLDER;
      break;
    case StudioSection.PUBLISHED:
      if (!isRootFolder && currentFolder) {
        currentSectionBiData.itemid = currentFolder.folderId;
        currentSectionBiData.typeid = ResourceType.GALLERY;
      } else if (tierId) {
        currentSectionBiData.itemid = tierId;
        currentSectionBiData.typeid = ResourceType.TIER;
      }
      break;
    default:
      break;
  }

  yield put(logPageView(currentSectionBiData));
}

function* handlePopup() {
  yield put(setNewModalLoading(false));
}

function* handleFetchBreadcrumbs({ payload: { folder } }: ReturnType<typeof fetchBreadcrumbs>) {
  const response: PapiStudioBreadcrumbs | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'breadcrumbs',
      data: {
        folderid: `${folder.folderId}`,
      } as PapiRequestStudioBreadcrumbs,
    },
    undefined,
    'studio'
  );

  if (!response || isApiError(response)) {
    yield put(addErrorNotification(i18next.t('common.somethingWentWrong'), NotificationDuration.S));
    return;
  }

  yield put(setBreadcrumbs(response.breadcrumbs));
}

export default function* pageSaga() {
  yield all([
    takeEvery([appMounted, biModalUnload], pageViewHandler),
    takeEvery(splashReady, handlePopup),
    takeEvery(fetchBreadcrumbs, handleFetchBreadcrumbs),
  ]);
}
